import { memo } from 'react';
import type { PatientTab } from '../../hooks/usePatientTabs';

// Deterministic color from patient ID — 8 distinct gradient pairs
const AVATAR_GRADIENTS = [
  'from-teal-500 to-cyan-600',
  'from-violet-500 to-purple-600',
  'from-rose-500 to-pink-600',
  'from-amber-500 to-orange-600',
  'from-emerald-500 to-green-600',
  'from-blue-500 to-indigo-600',
  'from-fuchsia-500 to-pink-600',
  'from-sky-500 to-blue-600',
];

function getGradient(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = ((hash << 5) - hash + id.charCodeAt(i)) | 0;
  }
  return AVATAR_GRADIENTS[Math.abs(hash) % AVATAR_GRADIENTS.length];
}

interface PatientTabBarProps {
  tabs: PatientTab[];
  activeTabId: string | null;
  onSwitchTab: (patientId: string) => void;
  onCloseTab: (patientId: string) => void;
  onAddTab: () => void;
}

const TabPill = memo(function TabPill({
  tab,
  isActive,
  onSwitch,
  onClose,
}: {
  tab: PatientTab;
  isActive: boolean;
  onSwitch: () => void;
  onClose: (e: React.MouseEvent) => void;
}) {
  const gradient = getGradient(tab.patientId);

  return (
    <button
      onClick={onSwitch}
      role="tab"
      aria-selected={isActive}
      aria-label={`${tab.firstName} ${tab.lastName}`}
      className={`
        group relative flex items-center gap-2 pl-1.5 pr-2 py-1 rounded-full
        transition-all duration-200 ease-out shrink-0 max-w-[200px]
        ${isActive
          ? 'bg-card ring-1 ring-teal-300 dark:ring-teal-500/40 shadow-lg shadow-teal-100/50 dark:shadow-teal-900/20 scale-[1.02]'
          : 'bg-muted hover:bg-card ring-1 ring-border hover:ring-border/60 hover:scale-[1.01]'
        }
      `}
    >
      {/* Avatar circle */}
      <div
        className={`
          w-6 h-6 rounded-full bg-gradient-to-br ${gradient}
          flex items-center justify-center text-[10px] font-bold text-white shrink-0
          ${isActive ? 'ring-2 ring-teal-400/40' : ''}
        `}
      >
        {tab.initials}
      </div>

      {/* Name */}
      <span className={`text-[11px] font-medium truncate ${
        isActive ? 'text-slate-900 dark:text-slate-100' : 'text-slate-500 dark:text-slate-400 group-hover:text-slate-800 dark:group-hover:text-slate-200'
      }`}>
        {tab.firstName} {tab.lastName?.charAt(0)}.
      </span>

      {/* Active indicator dot */}
      {isActive && (
        <div className="w-1.5 h-1.5 rounded-full bg-teal-400 shrink-0" />
      )}

      {/* Close button */}
      <span
        role="button"
        tabIndex={0}
        onClick={onClose}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClose(e as any); } }}
        aria-label={`Fermer l'onglet ${tab.firstName} ${tab.lastName}`}
        className={`
          shrink-0 w-5 h-5 rounded-full flex items-center justify-center
          transition-colors
          ${isActive
            ? 'text-slate-500 dark:text-slate-400 hover:text-red-400 hover:bg-red-900/30'
            : 'text-slate-400 dark:text-slate-600 opacity-0 group-hover:opacity-100 focus:opacity-100 hover:text-red-400 hover:bg-red-900/30'
          }
        `}
      >
        <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </span>
    </button>
  );
});

export default function PatientTabBar({
  tabs,
  activeTabId,
  onSwitchTab,
  onCloseTab,
  onAddTab,
}: PatientTabBarProps) {
  if (tabs.length === 0) return null;

  return (
    <div role="tablist" aria-label="Onglets patients" className="shrink-0 relative flex items-center gap-1.5 px-4 py-1.5 bg-card border-b border-border overflow-x-auto scrollbar-none">
      {/* Subtle top accent line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-teal-500/20 to-transparent" />
      {tabs.map((tab) => (
        <TabPill
          key={tab.patientId}
          tab={tab}
          isActive={tab.patientId === activeTabId}
          onSwitch={() => onSwitchTab(tab.patientId)}
          onClose={(e) => {
            e.stopPropagation();
            onCloseTab(tab.patientId);
          }}
        />
      ))}

      {/* Overflow fade hint when tabs overflow */}
      {tabs.length > 5 && (
        <div className="absolute right-12 top-0 bottom-0 w-8 bg-gradient-to-l from-white dark:from-slate-900 to-transparent pointer-events-none" />
      )}

      {/* Add new tab button */}
      <button
        onClick={onAddTab}
        aria-label="Ajouter un patient"
        className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center
          bg-muted hover:bg-muted/80 text-muted-foreground hover:text-teal-400
          transition-colors ring-1 ring-border hover:ring-teal-600/30
          focus:outline-none focus:ring-2 focus:ring-teal-500/50"
        title="Ouvrir un patient"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      </button>
    </div>
  );
}
